import { Alert, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDraggable } from "@dnd-kit/core";
import { useTranslation } from "i18n-fe";
import { END_RIDE_OUTCOMES } from "../util/endRide";

// #3293 - riders not drawn into any section yet. The same list serves two
// places: the "Not drawn" tab (draggable, so a rider can be pulled into a
// section tab) and the gate above Confirm and sign (not draggable), which
// lists the riders that still have to be marked No show or Scratched before
// the class can be finished. An undrawn rider stays PENDING and blocks
// auto-place forever (results.js classHasDefiniteBlockerRide), which is why
// each one must be ended here rather than left behind.
//
// The gate copy must NOT register a second draggable under the same id (the
// registry keeps the last one), so only the panel's items call useDraggable.
function DraggableUndrawnItem({ ride, locked, ...rest }) {
  const drag = useDraggable({ id: ride.id, disabled: locked });
  return <UndrawnItem ride={ride} drag={drag} locked={locked} {...rest} />;
}

function UndrawnItem({ ride, drag, locked, onEnd, busy, idPrefix = "undrawn" }) {
  const { t } = useTranslation("score");
  return (
    <Paper
      ref={drag?.setNodeRef}
      variant="outlined"
      data-testid={`${idPrefix}-row-${ride.competitorNumber ?? ride.id}`}
      sx={{
        p: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        opacity: drag?.isDragging ? 0.4 : 1,
      }}
    >
      {drag && (
        <Box
          ref={drag.setActivatorNodeRef}
          {...drag.attributes}
          {...(locked ? {} : drag.listeners)}
          aria-label={t("classGrid.sections.dragHandleLabel", { number: ride.competitorNumber ?? "" })}
          sx={{ display: "flex", alignItems: "center", touchAction: "none", cursor: "grab", minWidth: 40, minHeight: 40, justifyContent: "center" }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      <Typography variant="body1" fontWeight={700} sx={{ minWidth: 48 }}>
        {ride.competitorNumber ?? "—"}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 140 }}>
        <Typography variant="body2" fontWeight={600}>
          {ride.riderName ?? "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ride.horseName ?? "—"}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          disabled={busy || locked}
          onClick={() => onEnd(ride, END_RIDE_OUTCOMES.NO_SHOW)}
          sx={{ minHeight: 44, textTransform: "none" }}
          data-testid={`${idPrefix}-noshow-${ride.competitorNumber ?? ride.id}`}
        >
          {t("classGrid.sections.noShow")}
        </Button>
        <Button
          variant="outlined"
          disabled={busy || locked}
          onClick={() => onEnd(ride, END_RIDE_OUTCOMES.SCRATCH)}
          sx={{ minHeight: 44, textTransform: "none" }}
          data-testid={`${idPrefix}-scratch-${ride.competitorNumber ?? ride.id}`}
        >
          {t("classGrid.sections.scratched")}
        </Button>
      </Stack>
    </Paper>
  );
}

export function UndrawnPanel({ pool, ended, locked, onEnd, busyId, error, onAddToCurrent, canAdd }) {
  const { t } = useTranslation("score");
  return (
    <Box data-testid="undrawn-panel" sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} spacing={1}>
        <Typography variant="body2" color="text.secondary">
          {pool.length === 0 ? t("classGrid.sections.noneToAdd") : t("classGrid.sections.notDrawnHelp")}
        </Typography>
        {canAdd && pool.length > 0 && (
          <Button variant="contained" onClick={onAddToCurrent} sx={{ minHeight: 48, textTransform: "none" }} data-testid="undrawn-add-riders">
            {t("classGrid.sections.addRiders")}
          </Button>
        )}
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={1}>
        {pool.map((ride) => (
          <DraggableUndrawnItem key={ride.id} ride={ride} locked={locked} onEnd={onEnd} busy={busyId === ride.id} />
        ))}
      </Stack>
      {ended.length > 0 && (
        <Box sx={{ mt: 2 }} data-testid="ended-riders">
          <Typography variant="caption" color="text.secondary">
            {t("classGrid.sections.endedHeading")}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {ended.map((ride) => (
              <Stack key={ride.id} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 48 }}>
                  {ride.competitorNumber ?? "—"}
                </Typography>
                <Typography variant="body2">{ride.riderName ?? "—"}</Typography>
                <Chip size="small" label={t("classGrid.sections.endedChip")} />
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// The gate above Confirm and sign: every undrawn rider must be ended first.
export function UndrawnGate({ pool, onEnd, busyId, error }) {
  const { t } = useTranslation("score");
  if (!pool.length) return null;
  return (
    <Alert severity="warning" sx={{ mt: 2 }} data-testid="undrawn-gate" icon={false}>
      <Typography variant="subtitle1" fontWeight={700}>
        {t("classGrid.sections.gateTitle", { count: pool.length })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t("classGrid.sections.gateBody")}
      </Typography>
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Stack spacing={1}>
        {pool.map((ride) => (
          <UndrawnItem key={ride.id} ride={ride} drag={null} idPrefix="gate" onEnd={onEnd} busy={busyId === ride.id} />
        ))}
      </Stack>
    </Alert>
  );
}
